import { useCallback, useEffect, useState } from "react";
import { AuthContext } from "./auth.context";
import { ErrorStateObject, GetLoginCodeRequest, LoadingStateObject, LoginRequest, LoginResponse, TokenRequest } from "../../types/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { revokeSession, useAuthLoginMutation } from "../../api/auth";
import { useGoogleAuthenticationMutation } from "../../api/google";
import { useWebAuthnAuthenticationMutation } from "../../api/webauthn";
import { startAuthentication } from "@simplewebauthn/browser";
import { useCurrentUser } from "../../api/users";
import { clearAuthTokens, getAuthTokens, getTenantId, removeTenantId, saveTenantId, setAuthTokens } from "../../api/utils";
import { useSnackbar } from 'notistack';
import { useTranslation } from "react-i18next";
import { ERROR_SNACKBAR } from "../../consts";
import { CurrentUserResponse } from "../../types/users";

const BACKEND_RETRY_INTERVAL_MS = 10000;

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Resolve the auth state synchronously from the stored tokens on the very
  // first render. Starting optimistically at `true` made PublicRoute bounce a
  // logged-out visitor off /register (and /login) to /home with a `replace`
  // navigation before the check effect could run, discarding the URL - so a
  // link like /register?plan=<id> from the landing page never reached the
  // form and the visitor ended up on the in-app landing page instead.
  const [isSecure, setIsSecure] = useState<boolean>(() => {
    const tokens = getAuthTokens();
    return !!tokens?.access && !!tokens?.refresh && !!getTenantId();
  });
  const [currentUser, setCurrentUser] = useState<CurrentUserResponse | null | undefined>(undefined);
  const { loading: loginLoading, error: loginError, getCodeMutation, loginMutation, tokenMutation } = useAuthLoginMutation();
  const {
    loading: webauthnLoading,
    error: webauthnError,
    optionsMutation: webauthnOptionsMutation,
    verifyMutation: webauthnVerifyMutation,
  } = useWebAuthnAuthenticationMutation();
  const {
    loading: googleLoading,
    error: googleError,
    verifyMutation: googleVerifyMutation,
  } = useGoogleAuthenticationMutation();
  const { query: queryCurrentUser, loading: currentUserLoading, error: currentUserError } = useCurrentUser();

  const loadingState: LoadingStateObject = {
    login: loginLoading,
    currentUser: currentUserLoading,
    webauthn: webauthnLoading,
    google: googleLoading,
  };
  const errorState: ErrorStateObject = {
    login: loginError,
    currentUser: currentUserError,
    webauthn: webauthnError,
    google: googleError,
  };

  const getLoginCode = useCallback(
    async (data: GetLoginCodeRequest) => {
      return await getCodeMutation(data);
    },
    [getCodeMutation]
  );

  const getTenants = useCallback(
    async (loginData: LoginRequest) => {
      const { data, success } = await loginMutation(loginData);
      return { data, success };
    },
    [loginMutation]
  );

  const loginWithWebAuthn = useCallback(
    async (email: string): Promise<{ data: LoginResponse | undefined, success: boolean }> => {
      const { data: options, success: optionsSuccess } = await webauthnOptionsMutation(email);
      if (!optionsSuccess || !options) return { data: undefined, success: false };

      let credential;
      try {
        credential = await startAuthentication({ optionsJSON: options });
      } catch (e) {
        enqueueSnackbar(t("Biometric sign-in was cancelled or is not available on this device."), ERROR_SNACKBAR);
        return { data: undefined, success: false };
      }

      return await webauthnVerifyMutation(email, credential);
    },
    [webauthnOptionsMutation, webauthnVerifyMutation, enqueueSnackbar, t]
  );

  const loginWithGoogle = useCallback(
    async (credential: string): Promise<{ data: LoginResponse | undefined, success: boolean }> => {
      return await googleVerifyMutation(credential);
    },
    [googleVerifyMutation]
  );

  const refreshCurrentUser = useCallback(async () => {
    if (isSecure) {
      const data = await queryCurrentUser();
      setCurrentUser(data);
    }
  }, [isSecure, queryCurrentUser]);

  const setTenant = useCallback(
    async (requestData: TokenRequest, tenantid: string) => {
      saveTenantId(tenantid);
      const { data, success } = await tokenMutation(requestData);
      if (data && success) {
        setAuthTokens({ access: data.access, refresh: data.refresh })
        setIsSecure(true);
        // Fetch current user after setting tenant
        const userData = await queryCurrentUser();
        setCurrentUser(userData);
      };
    },
    [tokenMutation, queryCurrentUser]
  );

  const logout = useCallback(
    () => {
      revokeSession(getAuthTokens().refresh, getTenantId() || '');
      clearAuthTokens();
      removeTenantId();
      setIsSecure(false);
      setCurrentUser(undefined);
    },
    []
  );

  useEffect(() => {
    if (loginError && loginError.message) enqueueSnackbar(loginError.message, ERROR_SNACKBAR);
  }, [loginError, enqueueSnackbar]);

  useEffect(() => {
    if (webauthnError && webauthnError.message) enqueueSnackbar(webauthnError.message, ERROR_SNACKBAR);
  }, [webauthnError, enqueueSnackbar]);

  useEffect(() => {
    if (googleError && googleError.message) enqueueSnackbar(googleError.message, ERROR_SNACKBAR);
  }, [googleError, enqueueSnackbar]);

  useEffect(() => {
    const tokens = getAuthTokens();
    const isSecureCheck = !!tokens?.access && !!tokens?.refresh && !!getTenantId();
    setIsSecure(isSecureCheck);
    if (!isSecureCheck) logout();
  }, [location, logout, currentUser, currentUserLoading]);

  useEffect(() => {
    if (isSecure && location.pathname === '/login') navigate('/');
  }, [isSecure, location, navigate]);

  useEffect(() => {
    if (!currentUser && !currentUserLoading) {
      refreshCurrentUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Backend was unreachable (currentUser fetch failed) - keep retrying in the
  // background and reload the app once it responds again, so users don't have
  // to notice a deployment happened.
  useEffect(() => {
    if (!isSecure || currentUser !== null || currentUserLoading) return;

    const interval = setInterval(async () => {
      const data = await queryCurrentUser();
      if (data) {
        window.location.reload();
      }
    }, BACKEND_RETRY_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isSecure, currentUser, currentUserLoading, queryCurrentUser]);

  // Listen to profile and tenant update events to refresh current user
  useEffect(() => {
    const handleCurrentUserUpdated = () => {
      refreshCurrentUser();
    };

    window.addEventListener('profileUpdated', handleCurrentUserUpdated);
    window.addEventListener('tenantUpdated', handleCurrentUserUpdated);

    return () => {
      window.removeEventListener('profileUpdated', handleCurrentUserUpdated);
      window.removeEventListener('tenantUpdated', handleCurrentUserUpdated);
    };
  }, [refreshCurrentUser]);

  return (
    <AuthContext.Provider value={{
      getLoginCode,
      login: getTenants,
      loginWithWebAuthn,
      loginWithGoogle,
      setTenant,
      logout,
      isSecure,
      loadingState,
      errorState,
      currentUser,
      setCurrentUser,
      currentUserLoading,
      currentUserError,
      refreshCurrentUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
